import React, { useEffect, useState } from "react";
import { Menu } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import "./sidebar.css";
import GlobalIcon from "../Images/Globe-icon.png";
import ReportsIcon from "../Images/reportsIconBlack.png";
import TicketingSystemIcon from "../Images/ticketingSystemIconBlackColor.png";
import TicketingSystemRedIcon from "../Images/ticketingSystemIcon.png";
import DashboardRedIcon from "../Images/dashboardRedIcon.png";
import DashboardBlackIcon from "../Images/dashboardBlackIcon.png";
import GlobalAdminRedIcon from "../Images/GlobalAdminRedIcon.png";
import ReportsRedIcon from "../Images/powerBIRedIcon.png";
import ClanCrewIcon from "../Images/clan-crew-dashboard-logo.png";
import ClanCrewClientsBlack from "../Images/clan-crew-clients-blackIcon.png";
import ClanCrewClientsRed from "../Images/clan-crew-clients-RedIcon.png";
import ClanCrewNotificationBlack from "../Images/clan-crew-notification-blackIcon.png";
import ClanCrewNotificationRed from "../Images/clan-crew-notification-RedIcon.png";
import ClanCrewAnalyticsRed from "../Images/clan-crew-analytics-RedIcon.png";
import ClanCrewAnalyticsBlack from "../Images/clan-crew-analytics-blackIcon.png";
import { currentUserProfileQuery } from "../services/userAPI";

const Sidebar = () => {
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedKey, setSelectedKey] = useState("1");
    const [userRole, setUserRole] = useState(null);

    const { data: userProfileData, loading: profileLoading } = useQuery(currentUserProfileQuery, {
        errorPolicy: 'all'
    });

    // === MENU KEY MAPS ===
    const defaultMenuKeyMap = {
        "^/dashboard": "1",
        "^/global-admin": "4",
        "^/ticketing-system": "6",
        "^/reports": "5",
    };

    const adminKeyMap = {
        "^/admin-dashboard": "1",
        "^/reports": "2",
        "^/ticketing-system": "6",
    };

    const clanCrewMenuKeyMap = {
        "^/clan-crew-dashboard": "1",
        "^/clan-crew-clients": "2",
        "^/clan-crew-notification": "3",
        "^/clan-crew-analytics": "4",
    };

    // Set user role
    useEffect(() => {
        if (userProfileData?.currentUserProfile?.role) {
            setUserRole(userProfileData.currentUserProfile.role);
        }
    }, [userProfileData]);

    // === REDIRECT BASED ON ROLE ===
    useEffect(() => {
        if (!userRole) return;

        const isClanCrew = userRole.toLowerCase().includes("clan crew") || userRole.toLowerCase() === "clancrew";
        const isAdmin = userRole.toLowerCase() === "admin";

        if (
            location.pathname === "/" ||
            (location.pathname === "/dashboard" && (isClanCrew || isAdmin)) ||
            (location.pathname.startsWith("/clan-crew") && !isClanCrew)
        ) {
            if (isClanCrew) {
                navigate("/clan-crew-dashboard", { replace: true });
            } else if (isAdmin) {
                navigate("/admin-dashboard", { replace: true });
            } else {
                navigate("/dashboard", { replace: true });
            }
        }
    }, [userRole, location.pathname, navigate]);

    // === SET SELECTED KEY BASED ON ROUTE & ROLE ===
    useEffect(() => {
        if (!userRole) return;

        const isClanCrew = userRole.toLowerCase().includes("clan crew") || userRole.toLowerCase() === "clancrew";
        const isAdmin = userRole.toLowerCase() === "admin";

        let menuKeyMap;
        if (isClanCrew) {
            menuKeyMap = clanCrewMenuKeyMap;
        } else if (isAdmin) {
            menuKeyMap = adminKeyMap;
        } else {
            menuKeyMap = defaultMenuKeyMap;
        }

        let foundKey = null;
        for (const [pattern, key] of Object.entries(menuKeyMap)) {
            if (location.pathname.match(pattern)) {
                foundKey = key;
                break;
            }
        }

        setSelectedKey(foundKey || "1");
    }, [location.pathname, userRole]);

    // === HIDDEN PATHS (No Sidebar) ===
    const hiddenPaths = [
        "/create", "/create-client-form", "/view-role", "/user-management",
        "/ag-grid", "/calendar-view", "/bulk-leads-result", "/target-funnel-preview",
        "/lead-indicator-selection", "/attendance-daily-norm", "/clan-management",
        "/add-stars", "/add-content-library", "/more-videos", "/activities",
        "/statuses", "/stages", "/productConfiguration", "/masterConfiguration",
        "/role-management", "/role-management-view", "/clan-crew-user-management",
        "/product-updates", "/admin-view-role", "/admin-product-configuration",
        "/admin-attendance-daily-norm", "/admin-stages", "/admin-statuses",
        "/admin-activities", "/admin-user-management", "/admin-masters-configuration",
        "/admin-clan-management", "/admin-add-stars", "/admin-add-content-library",
        "/admin-more-videos", "/forms-configuration", "/faqs", "/admin-faq", "/mark-holidays",
        "/productsConfiguration", "/products-Configuration", "/experience-configuration",
        "/clan-management/stars", "/clan-management/content-library", "/clan-management/product-updates",
        "/sync-configuration", "/syncAddStar", "/assessment-configuration", "/demographicjp-fitment-scenario",
        "/scenario-based-assessment", "/Add-Quize", "/star-video-configuration", "/assessment-journey",
        "/Add-Scenario", "/Add-Quiz", "/role-details-configuration", "/add-job-details", "/jp-fitment-test",
        "/scenario-based-assessment", "/demographicjp-fitment-scenario", "/add-quiz", "/add-scenario",
        "/assessment-journey", "/star-video-configuration", "/add-job-details", "/add-client-and-role",
        "/clan-lite-growth-dashboard", "/role-master-configuration", "/clan-management/training", "/add-course-module",
        "/course-module-detail", "/add-course-video", "/clan-management/business-understanding",

    ];

    if (hiddenPaths.includes(location.pathname) || profileLoading || !userRole) {
        return null;
    }

    if (location.pathname.match(/^\/user\/[^/]+$/)) return null;
    if (location.pathname.match(/^\/admin-user\/[^/]+$/)) return null;
    if (location.pathname.match(/^\/clan-crew-user-profile\/[^/]+$/)) return null;
    if (location.pathname.match(/^\/understand-user\/[^/]+$/)) return null;
    if (location.pathname.match(/^\/edit-client-and-role\/[^/]+$/)) return null;

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
    const handleMenuClick = (path) => {
        navigate(path);
    };

    const isClanCrew = userRole?.toLowerCase().includes("clan crew") || userRole?.toLowerCase() === "clancrew";
    const isAdmin = userRole?.toLowerCase() === "admin";

    return (
        <div style={{ display: "flex", flexDirection: "row" }}>
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    height: "100%",
                    position: "fixed",
                    left: 0,
                    // zIndex: 1000000000,
                    width: isHovered ? "170px" : "70px",
                    background: "white",
                }}
                className={`sider ${isHovered ? 'expanded' : 'collapsed'}`}
            >
                {/* === CLAN CREW MENU === */}
                {isClanCrew && (
                    <Menu mode="inline" selectedKeys={[selectedKey]} style={{ border: "none", background: "none", top: "2%" }}>
                        <Menu.Item key="1" onClick={() => handleMenuClick('/clan-crew-dashboard')} icon={
                            <img src={selectedKey === "1" ? DashboardRedIcon : DashboardBlackIcon} alt="Dashboard" style={{ width: "20px", height: "20px" }} />
                        }>
                            <Link to="/clan-crew-dashboard" style={{ fontWeight: selectedKey === "1" ? "bold" : "normal", color: "#000", opacity: isHovered ? 1 : 0 }}>
                                Dashboard
                            </Link>
                        </Menu.Item>
                        {/* <Menu.Item key="2" onClick={() => handleMenuClick('/clan-crew-clients')} icon={
                            <img src={selectedKey === "2" ? ClanCrewClientsRed : ClanCrewClientsBlack} alt="Clients" style={{ width: "20px", height: "20px" }} />
                        }>
                            <Link to="/clan-crew-clients" style={{ fontWeight: selectedKey === "2" ? "bold" : "normal", color: "#000", opacity: isHovered ? 1 : 0 }}>
                                Clients
                            </Link>
                        </Menu.Item>
                        <Menu.Item key="3" onClick={() => handleMenuClick('/clan-crew-notification')} icon={
                            <img src={selectedKey === "3" ? ClanCrewNotificationRed : ClanCrewNotificationBlack} alt="Notification" style={{ width: "20px", height: "20px" }} />
                        }>
                            <Link to="/clan-crew-notification" style={{ fontWeight: selectedKey === "3" ? "bold" : "normal", color: "#000", opacity: isHovered ? 1 : 0 }}>
                                Notification
                            </Link>
                        </Menu.Item>
                        <Menu.Item key="4" onClick={() => handleMenuClick('/clan-crew-analytics')} icon={
                            <img src={selectedKey === "4" ? ClanCrewAnalyticsRed : ClanCrewAnalyticsBlack} alt="Analytics" style={{ width: "20px", height: "20px" }} />
                        }>
                            <Link to="/clan-crew-analytics" style={{ fontWeight: selectedKey === "4" ? "bold" : "normal", color: "#000", opacity: isHovered ? 1 : 0 }}>
                                Analytics
                            </Link>
                        </Menu.Item> */}
                    </Menu>
                )}

                {/* === ADMIN MENU === */}
                {isAdmin && (
                    <Menu mode="inline" selectedKeys={[selectedKey]} style={{ border: "none", background: "none", top: "2%" }}>
                        <Menu.Item key="1" onClick={() => handleMenuClick('/admin-dashboard')} icon={
                            <img src={selectedKey === "1" ? DashboardRedIcon : DashboardBlackIcon} alt="Admin Dashboard" style={{ width: "20px", height: "20px" }} />
                        }>
                            <Link to="/admin-dashboard" style={{ fontWeight: selectedKey === "1" ? "bold" : "normal", color: "#000", opacity: isHovered ? 1 : 0 }}>
                                Dashboard
                            </Link>
                        </Menu.Item>
                        <Menu.Item key="2" onClick={() => handleMenuClick('/admin-reports')} icon={
                            <img src={selectedKey === "2" ? ReportsRedIcon : ReportsIcon} alt="Reports" style={{ width: "20px", height: "20px" }} />
                        }>
                            <Link to="/admin-reports" style={{ fontWeight: selectedKey === "2" ? "bold" : "normal", color: "#000", opacity: isHovered ? 1 : 0 }}>
                                Reports
                            </Link>
                        </Menu.Item>
                    </Menu>
                )}

                {/* === DEFAULT MENU (Non-Admin, Non-ClanCrew) === */}
                {!isClanCrew && !isAdmin && (
                    <Menu mode="inline" selectedKeys={[selectedKey]} style={{ border: "none", background: "none", top: "2%" }}>
                        <Menu.Item
                            key="1"
                            onClick={() => handleMenuClick('/dashboard')}
                            icon={
                                <img
                                    src={selectedKey === "1" ? DashboardRedIcon : DashboardBlackIcon}
                                    alt="Dashboard"
                                    style={{ width: "20px", height: "20px" }}
                                />
                            }
                        >
                            <Link
                                to="/dashboard"
                                style={{
                                    fontWeight: selectedKey === "1" ? "bold" : "normal",
                                    color: selectedKey === "1" ? "#AD0101" : "#000",
                                    opacity: isHovered ? 1 : 0
                                }}
                            >
                                Dashboard
                            </Link>
                        </Menu.Item>

                        <Menu.Item
                            key="4"
                            onClick={() => handleMenuClick('/global-admin')}
                            icon={
                                <img
                                    src={selectedKey === "4" ? GlobalAdminRedIcon : GlobalIcon}
                                    alt="Global Admin"
                                    style={{ width: "20px", height: "20px" }}
                                />
                            }
                        >
                            <Link
                                to="/global-admin"
                                style={{
                                    fontWeight: selectedKey === "4" ? "bold" : "normal",
                                    color: selectedKey === "4" ? "#AD0101" : "#000",
                                    opacity: isHovered ? 1 : 0
                                }}
                            >
                                Global Admin
                            </Link>
                        </Menu.Item>

                        <Menu.Item
                            key="6"
                            onClick={() => handleMenuClick('/ticketing-system')}
                            icon={
                                <img
                                    src={selectedKey === "6" ? TicketingSystemRedIcon : TicketingSystemIcon}
                                    alt="CS Desk"
                                    style={{ width: "20px", height: "20px" }}
                                />
                            }
                        >
                            <Link
                                to="/ticketing-system"
                                style={{
                                    fontWeight: selectedKey === "6" ? "bold" : "normal",
                                    color: selectedKey === "6" ? "#AD0101" : "#000",
                                    opacity: isHovered ? 1 : 0
                                }}
                            >
                                CS Desk
                            </Link>
                        </Menu.Item>

                        <Menu.Item
                            key="5"
                            onClick={() => handleMenuClick('/reports')}
                            icon={
                                <img
                                    src={selectedKey === "5" ? ReportsRedIcon : ReportsIcon}
                                    alt="Reports"
                                    style={{ width: "20px", height: "20px" }}
                                />
                            }
                        >
                            <Link
                                to="/reports"
                                style={{
                                    fontWeight: selectedKey === "5" ? "bold" : "normal",
                                    color: selectedKey === "5" ? "#AD0101" : "#000",
                                    opacity: isHovered ? 1 : 0
                                }}
                            >
                                Reports
                            </Link>
                        </Menu.Item>
                    </Menu>
                )}
            </div>
        </div>
    );
};

export default Sidebar;